import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
} from "react-router-dom";
import { Suspense } from "react";
import * as Sentry from "@sentry/react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { createTheme, ThemeProvider } from "@mui/material";
import { GoogleOAuthProvider } from "@react-oauth/google";
import HomePage from "./pages/HomePage/HomePage";
import AppLayout from "./common-components/AppLayout";
import CookieConsentBanner from "./common-components/CookieConsentBanner";
import Login from "./pages/Login/Login";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import AuthContextProvider from "./contexts/AuthenticationContext";
import ProtectedRoute from "./common-components/ProtectedRoute";
import GuestRoute from "./common-components/GuestRoute";
import ErrorBoundary from "./common-components/ErrorBoundary";
import AdminRoute from "./common-components/AdminRoute";
import ProductDetails from "./pages/Product/ProductDetails";
import ShopPage from "./pages/Shop/ShopPage";
import UserPublicProfile from "./pages/UserProfile/UserPublicProfile";
import { frontendRoutes } from "./utility/frontendRoutes";
import UserCart from "./pages/UserCart/UserCart";
import UserProfile from "./pages/UserProfile/UserProfile";
import AccountSettings from "./pages/UserProfile/AccountSettings";
import NotificationSettings from "./pages/UserProfile/NotificationSettings";
import AdminLayout from "./pages/Admin/AdminLayout";
import AdminDashboard from "./pages/Admin/AdminDashboard";
import Analytics from "./pages/Admin/analytics/Analytics";
import DisputeList from "./pages/Admin/disputes/DisputeList";
import DisputeDetail from "./pages/Admin/disputes/DisputeDetail";
import ReportList from "./pages/Admin/reports/ReportList";
import ReportDetail from "./pages/Admin/reports/ReportDetail";
import UserList from "./pages/Admin/users/UserList";
import UserDetail from "./pages/Admin/users/UserDetail";
import ShopList from "./pages/Admin/shops/ShopList";
import ShopDetail from "./pages/Admin/shops/ShopDetail";
import OrderList from "./pages/Admin/orders/OrderList";
import OrderDetail from "./pages/Admin/orders/OrderDetail";
import ProductList from "./pages/Admin/products/ProductList";
import ProductDetail from "./pages/Admin/products/ProductDetail";
import VerificationList from "./pages/Admin/verifications/VerificationList";
import VerificationDetail from "./pages/Admin/verifications/VerificationDetail";
import FeatureFlagList from "./pages/Admin/featureFlags/FeatureFlagList";
import FeedbackList from "./pages/Admin/feedback/FeedbackList";
import FollowListPage from "./pages/FollowList/FollowListPage";
import AllReviewsPage from "./pages/Reviews/AllReviewsPage";
import SearchPage from "./pages/Search/SearchPage";
import { NotFoundPage } from "./common-components/NotFoundPage";
import OpenInApp from "./pages/AppLink/OpenInApp";
import LandingPage from "./pages/Landing/LandingPage";
import LandingLayout from "./pages/Landing/LandingLayout";
import WhatIsNgwaahia from "./pages/About/WhatIsNgwaahia";
import lazyWithReload from "./utility/lazyWithReload";

// Lazy-loaded: these pull in react-markdown, fuse.js and every compiled
// article, which together are larger than the rest of the app's page code and
// are needed only by visitors who actually open the docs.
const LegalDoc = lazyWithReload(() => import("./pages/Legal/LegalDoc"));
const HelpHome = lazyWithReload(() => import("./pages/Help/HelpHome"));
const HelpCategory = lazyWithReload(() => import("./pages/Help/HelpCategory"));
const HelpArticle = lazyWithReload(() => import("./pages/Help/HelpArticle"));
const StoryPage = lazyWithReload(() => import("./pages/Stories/StoryPage"));
const CreditsPage = lazyWithReload(() => import("./pages/Credits/CreditsPage"));

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: false } },
});

// react-router requires children of an explicit-path parent route with a
// trailing "/*" (like the admin portal below) to use paths relative to that
// parent, not their own full frontendRoutes value — this derives that
// relative segment from the constant instead of a bare hardcoded string.
const adminChildPath = (fullPath) =>
  fullPath.replace(`${frontendRoutes.ADMIN_ROOT}/`, "");

const theme = createTheme({
  typography: {
    fontFamily: '"DM Sans", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: { root: { textTransform: "none" } },
    },
  },
});

function App() {
  return (
    <GoogleOAuthProvider
      clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ""}
    >
      <HelmetProvider>
        {/* Fallback title. Helmet only writes document.title while some
            mounted instance sets one, so without this, leaving a page that
            sets its own title (product, shop, ...) for one that doesn't
            leaves the previous page's title in the tab. */}
        <Helmet>
          <title>Ngwaahia | Buy & Sell With Local Sellers</title>
        </Helmet>
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <Router>
              <AuthContextProvider>
                <ErrorBoundary>
                  {/* Only the lazily-loaded docs pages suspend; everything else
                is in the main bundle and renders without ever hitting this
                fallback. */}
                  <Suspense fallback={null}>
                    <Routes>
                      {/* Landing page — bypasses AppLayout, has its own nav/footer.
                  Redirects a logged-in visitor to the feed on its own (see
                  LANDING_STAY_QUERY_PARAM in frontendRoutes), rather than
                  using the generic GuestRoute — it needs an explicit bypass
                  so AppFooter's "back to landing" link actually works for a
                  logged-in user instead of bouncing them right back. */}
                      <Route
                        path={frontendRoutes.LANDING}
                        element={<LandingPage />}
                      />
                      {/* Admin portal — bypasses AppLayout. This parent needs the
                  trailing "/*" (an explicit-path parent with nested children
                  requires it), which means react-router requires children to
                  use paths relative to it — an absolute child path must
                  literally start with "/admin/*", which none of the real
                  admin pages do. adminChildPath derives that relative segment
                  from the frontendRoutes constant instead of a bare string,
                  so it still can't drift from the values used to build admin
                  nav links elsewhere. */}
                      <Route
                        path={`${frontendRoutes.ADMIN_ROOT}/*`}
                        element={
                          <AdminRoute>
                            <AdminLayout />
                          </AdminRoute>
                        }
                      >
                        <Route index element={<AdminDashboard />} />
                        <Route
                          path={adminChildPath(frontendRoutes.ADMIN_ANALYTICS)}
                          element={<Analytics />}
                        />
                        <Route
                          path={adminChildPath(frontendRoutes.ADMIN_DISPUTES)}
                          element={<DisputeList />}
                        />
                        <Route
                          path={adminChildPath(
                            frontendRoutes.ADMIN_DISPUTE_DETAIL,
                          )}
                          element={<DisputeDetail />}
                        />
                        <Route
                          path={adminChildPath(frontendRoutes.ADMIN_REPORTS)}
                          element={<ReportList />}
                        />
                        <Route
                          path={adminChildPath(
                            frontendRoutes.ADMIN_REPORT_DETAIL,
                          )}
                          element={<ReportDetail />}
                        />
                        <Route
                          path={adminChildPath(frontendRoutes.ADMIN_USERS)}
                          element={<UserList />}
                        />
                        <Route
                          path={adminChildPath(
                            frontendRoutes.ADMIN_USER_DETAIL,
                          )}
                          element={<UserDetail />}
                        />
                        <Route
                          path={adminChildPath(frontendRoutes.ADMIN_SHOPS)}
                          element={<ShopList />}
                        />
                        <Route
                          path={adminChildPath(
                            frontendRoutes.ADMIN_SHOP_DETAIL,
                          )}
                          element={<ShopDetail />}
                        />
                        <Route
                          path={adminChildPath(frontendRoutes.ADMIN_ORDERS)}
                          element={<OrderList />}
                        />
                        <Route
                          path={adminChildPath(
                            frontendRoutes.ADMIN_ORDER_DETAIL,
                          )}
                          element={<OrderDetail />}
                        />
                        <Route
                          path={adminChildPath(frontendRoutes.ADMIN_PRODUCTS)}
                          element={<ProductList />}
                        />
                        <Route
                          path={adminChildPath(
                            frontendRoutes.ADMIN_PRODUCT_DETAIL,
                          )}
                          element={<ProductDetail />}
                        />
                        <Route
                          path={adminChildPath(
                            frontendRoutes.ADMIN_VERIFICATIONS,
                          )}
                          element={<VerificationList />}
                        />
                        <Route
                          path={adminChildPath(
                            frontendRoutes.ADMIN_VERIFICATION_DETAIL,
                          )}
                          element={<VerificationDetail />}
                        />
                        <Route
                          path={adminChildPath(
                            frontendRoutes.ADMIN_FEATURE_FLAGS,
                          )}
                          element={<FeatureFlagList />}
                        />
                        <Route
                          path={adminChildPath(frontendRoutes.ADMIN_FEEDBACK)}
                          element={<FeedbackList />}
                        />
                      </Route>
                      {/* Universal-link fallbacks for mobile-only screens — bypass AppLayout, no NavBar */}
                      <Route
                        path={frontendRoutes.OPEN_IN_APP_VERIFY_PHONE}
                        element={<OpenInApp />}
                      />
                      <Route
                        path={frontendRoutes.OPEN_IN_APP_ADD_ADDRESS}
                        element={<OpenInApp />}
                      />
                      <Route
                        path={frontendRoutes.OPEN_IN_APP_INTERESTS}
                        element={<OpenInApp />}
                      />
                      <Route
                        path={frontendRoutes.OPEN_IN_APP_EDIT_PROFILE}
                        element={<OpenInApp />}
                      />
                      <Route
                        path={frontendRoutes.OPEN_IN_APP_SETUP_SHOP}
                        element={<OpenInApp />}
                      />
                      <Route
                        path={frontendRoutes.OPEN_IN_APP_MY_SHOP}
                        element={<OpenInApp />}
                      />
                      <Route
                        path={frontendRoutes.OPEN_IN_APP_ORDER}
                        element={<OpenInApp />}
                      />
                      <Route
                        path={frontendRoutes.OPEN_IN_APP_SHOP_ORDER}
                        element={<OpenInApp />}
                      />
                      {/* Public info pages — share the landing page's nav/footer
                  instead of AppLayout's NavBar */}
                      <Route element={<LandingLayout />}>
                        <Route
                          path={frontendRoutes.WHAT_IS_NGWAAHIA}
                          element={<WhatIsNgwaahia />}
                        />
                        {/* These four URLs are submitted to both app stores and
                    hardcoded in the mobile app's agreement modals — the
                    components behind them may change, the paths may not. */}
                        <Route
                          path={frontendRoutes.PRIVACY_POLICY}
                          element={<LegalDoc id="privacy-policy" />}
                        />
                        <Route
                          path={frontendRoutes.TERMS_OF_SERVICE}
                          element={<LegalDoc id="terms-of-service" />}
                        />
                        <Route
                          path={frontendRoutes.DELETE_ACCOUNT}
                          element={<LegalDoc id="delete-account" />}
                        />
                        <Route
                          path={frontendRoutes.SUPPORT}
                          element={<LegalDoc id="support" />}
                        />
                        <Route
                          path={frontendRoutes.HELP}
                          element={<HelpHome />}
                        />
                        <Route
                          path={frontendRoutes.HELP_CATEGORY}
                          element={<HelpCategory />}
                        />
                        <Route
                          path={frontendRoutes.HELP_ARTICLE}
                          element={<HelpArticle />}
                        />
                        <Route
                          path={frontendRoutes.STORY}
                          element={<StoryPage />}
                        />
                        <Route
                          path={frontendRoutes.CREDITS}
                          element={<CreditsPage />}
                        />
                        <Route
                          path={frontendRoutes.CREDITS_PERSON}
                          element={<CreditsPage />}
                        />
                        {/* The app-wide catch-all lives inside AppLayout, so
                    without this a bad docs URL would render the logged-in
                    NavBar instead of the landing chrome. */}
                        <Route path="/help/*" element={<NotFoundPage />} />
                        <Route path="/stories/*" element={<NotFoundPage />} />
                      </Route>
                      {/* Regular app — wrapped in AppLayout with NavBar */}
                      <Route
                        element={
                          <AppLayout>
                            <Outlet />
                          </AppLayout>
                        }
                      >
                        <Route
                          path={frontendRoutes.HOME}
                          element={<HomePage />}
                        />
                        <Route
                          path={frontendRoutes.SEARCH}
                          element={<SearchPage />}
                        />
                        <Route
                          path={frontendRoutes.LOGIN}
                          element={
                            <GuestRoute>
                              <Login />
                            </GuestRoute>
                          }
                        />
                        <Route
                          path={frontendRoutes.VIEW_PRODUCT}
                          element={<ProductDetails />}
                        />
                        <Route
                          path={frontendRoutes.VIEW_SHOP}
                          element={<ShopPage />}
                        />
                        <Route
                          path={frontendRoutes.VIEW_USER}
                          element={<UserPublicProfile />}
                        />
                        <Route
                          path={frontendRoutes.USER_FOLLOWERS}
                          element={<FollowListPage initialTab="followers" />}
                        />
                        <Route
                          path={frontendRoutes.USER_FOLLOWING}
                          element={<FollowListPage initialTab="following" />}
                        />
                        <Route
                          path={frontendRoutes.SHOP_FOLLOWERS}
                          element={<FollowListPage initialTab="followers" />}
                        />
                        <Route
                          path={frontendRoutes.PRODUCT_REVIEWS}
                          element={<AllReviewsPage entityType="product" />}
                        />
                        <Route
                          path={frontendRoutes.SHOP_REVIEWS}
                          element={<AllReviewsPage entityType="shop" />}
                        />
                        <Route
                          path={frontendRoutes.MY_CART}
                          element={<UserCart />}
                        />
                        <Route
                          path={frontendRoutes.USER_PROFILE}
                          element={
                            <ProtectedRoute>
                              <UserProfile />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path={frontendRoutes.ACCOUNT_SETTINGS}
                          element={
                            <ProtectedRoute>
                              <AccountSettings />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path={frontendRoutes.NOTIFICATION_SETTINGS}
                          element={
                            <ProtectedRoute>
                              <NotificationSettings />
                            </ProtectedRoute>
                          }
                        />
                        {/* Catch-all — any unrecognized route shows 404 */}
                        <Route path="*" element={<NotFoundPage />} />
                      </Route>
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
                {/* Sibling to the Routes, not nested in AppLayout — the
              landing/info pages and the admin portal bypass AppLayout (see
              above) but must still show this. */}
                <CookieConsentBanner />
              </AuthContextProvider>
            </Router>
          </QueryClientProvider>
        </ThemeProvider>
      </HelmetProvider>
    </GoogleOAuthProvider>
  );
}

export default Sentry.withProfiler(App);
